import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Check, FileText, MapPin, BookOpen, TrendingUp, Globe } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const translations = {
  overline: { es: "Valor adicional", en: "Added Value" },
  title: { es: "En cada curso obtienes:", en: "Each Course Includes:" },
  cardText: { es: "Recursos exclusivos en cada curso", en: "Exclusive resources in each course" },
  bonuses: {
    es: [
      "Recursos descargables para aplicar lo aprendido",
      "Casos reales de eventos y destinos en Latinoamérica",
      "Guías prácticas para el desarrollo de productos y experiencias",
      "Actualizaciones de contenido con tendencias del sector",
      "Enfoque alineado con estándares internacionales",
    ],
    en: [
      "Downloadable resources to apply what you learn",
      "Real cases from events and destinations in Latin America",
      "Practical guides for product and experience development",
      "Content updates with industry trends",
      "Approach aligned with international standards",
    ],
  },
};

const BonusSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const bonuses = t(translations.bonuses);

  return (
    <section data-testid="bonus-section" ref={ref} className="py-24 md:py-32 bg-[#0B1F3B]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div initial={{ opacity: 0, x: -40 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8 }}>
            <span className="inline-flex items-center gap-2 text-[#2ED3C6] text-xs md:text-sm uppercase tracking-[0.2em] font-bold font-body mb-4">
              <span className="w-8 h-[2px] bg-[#2ED3C6]" />
              {t(translations.overline)}
            </span>
            <h2 data-testid="bonus-title" className="font-display text-3xl md:text-4xl lg:text-5xl text-white font-medium tracking-tight mb-8">
              {t(translations.title)}
            </h2>
            <div className="space-y-5">
              {bonuses.map((bonus, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  data-testid={`bonus-item-${index}`}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-10 h-10 bg-[#2ED3C6] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-body text-white/80 text-lg leading-relaxed pt-1.5">{bonus}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 40 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8 }} className="relative">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden">
                <img src="https://images.unsplash.com/photo-1768508665663-fa483a0cb208?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBuZXR3b3JraW5nJTIwZXZlbnR8ZW58MHx8fHwxNzc1NzU1MTExfDA&ixlib=rb-4.1.0&q=85" alt="Networking en evento profesional MICE en Latinoamérica" width="800" height="400" loading="lazy" decoding="async" className="w-full h-[400px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3B]/80 to-transparent" />
              </div>
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.6, delay: 0.6 }} className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-2xl max-w-[240px]">
                <div className="flex items-center gap-3 mb-2">
                  {[FileText, MapPin, BookOpen].map((Icon, i) => (
                    <div key={i} className="w-8 h-8 bg-[#0B1F3B] rounded-lg flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[#2ED3C6]" />
                    </div>
                  ))}
                </div>
                <p className="font-body text-[#0B1F3B] text-sm font-medium">{t(translations.cardText)}</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BonusSection;
